import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Paper,
    CircularProgress,
    Alert,
    Divider,
    Stack,
    Chip,
} from "@mui/material";
import { BeachAccess as HolidaysIcon } from "@mui/icons-material";
import ScheduleService from "../services/Schedule.service";

const year = new Date().getFullYear();

function isPast(dateStr) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
}

function formatDate(dateStr) {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
}

export default function Holidays() {
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        ScheduleService.getHolidaysByYear(year)
            .then((res) => setHolidays(res.data.results ?? res.data))
            .catch(() => setError("Failed to load holidays."))
            .finally(() => setLoading(false));
    }, []);

    return (
        <Box>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: 1,
                    p: 2.5,
                    mb: 2,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <HolidaysIcon sx={{ fontSize: 36, color: "secondary.main" }} />
                    <Box>
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                            {year} Holidays
                        </Typography>
                        {!loading && holidays.length > 0 && (
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                <Chip
                                    label={`${holidays.length} days`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: "0.7rem" }}
                                />
                                <Chip
                                    label={`${holidays.reduce((sum, h) => sum + (h.half_day ? 4 : 8), 0)} hrs`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: "0.7rem" }}
                                />
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : holidays.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 6,
                        textAlign: "center",
                    }}
                >
                    <HolidaysIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                        No holidays found for {year}.
                    </Typography>
                </Paper>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                    }}
                >
                    <List disablePadding>
                        {holidays.map((holiday, index) => {
                            const past = isPast(holiday.date);
                            return (
                                <React.Fragment key={holiday.id}>
                                    {index > 0 && <Divider />}
                                    <ListItem sx={{ px: 2, py: 1.5 }}>
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <span>{holiday.holiday_name?.name ?? holiday.name}</span>
                                                    {holiday.half_day && (
                                                        <Chip
                                                            label="Half Day"
                                                            size="small"
                                                            color="secondary"
                                                            variant="outlined"
                                                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                                                        />
                                                    )}
                                                </Stack>
                                            }
                                            secondary={formatDate(holiday.date)}
                                            primaryTypographyProps={{
                                                fontFamily: "Montserrat, sans-serif",
                                                fontWeight: past ? 400 : 600,
                                                color: past ? "text.disabled" : "text.primary",
                                                sx: past ? { textDecoration: "line-through" } : {},
                                                component: "div",
                                            }}
                                            secondaryTypographyProps={{
                                                fontFamily: "Montserrat, sans-serif",
                                                color: past ? "text.disabled" : "text.secondary",
                                            }}
                                        />
                                    </ListItem>
                                </React.Fragment>
                            );
                        })}
                    </List>
                </Paper>
            )}

            {/* Policy note */}
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, fontFamily: "Montserrat, sans-serif", lineHeight: 1.6 }}
            >
                To qualify for holiday pay, full-time employees and non-exempt employees
                must have worked or been paid for the scheduled workday before and after
                the holiday.
            </Typography>
        </Box>
    );
}
